/**
 * UnifiedOnboardingModal
 * Single role-driven onboarding wizard replacing all existing onboarding flows.
 *
 * Props:
 *   userRole — 'contractor' | 'verifier' | 'professional_verifier' | 'quality_inspector'
 *
 * Visual template: VerifierOnboardingWizard.js (MUI Dialog + Stepper + DialogActions)
 * Color: teal #43B581 throughout — no purple.
 *
 * Step counts (shown in progress bar):
 *   contractor                            → 6 steps  (Choose Tier + 5 others)
 *   verifier                              → 3 steps  (Basic Info, Specializations, Profile Photo)
 *   professional_verifier, quality_inspector → 5 steps  (no tier step — role was set at registration)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Stepper, Step, StepLabel,
  TextField, Chip, Card, CardContent, Paper, Grid,
  CircularProgress, Alert, IconButton, Divider, LinearProgress,
  FormControlLabel, Checkbox, Tooltip,
} from '@mui/material';
import PhoneField from '../common/PhoneField';
import {
  CheckCircle, ArrowBack, ArrowForward, Edit, Add, Delete,
  PersonOutline, WorkspacePremium, Engineering, CloudUpload, AutoAwesome,
  Lock, Close,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { brand } from '../../theme/colors';
import ZimAddressFields from '../common/ZimAddressFields';
import LocationInput from '../common/LocationInput';
import { minSpecializations } from '../../utils/onboardingRequirements';
import PortfolioReferencesStep from './steps/PortfolioReferencesStep';

// ─── PALETTE ──────────────────────────────────────────────────────────────────

const TEAL       = '#43B581';
const TEAL_DARK  = '#3AA76D';
const TEAL_LIGHT = '#E8F8F0';

// ─── DATA ─────────────────────────────────────────────────────────────────────

/** 46 contractor specializations (32 original + 14 extended trades) */
const CONTRACTOR_SPECIALIZATIONS = [
  // General Building
  'General Building', 'Residential Construction', 'Commercial Construction',
  // Structural & Foundation
  'Foundation Work', 'Structural Engineering', 'Masonry & Brickwork', 'Concrete Work',
  // Roofing & Exterior
  'Roofing', 'Thatching', 'Guttering & Drainage',
  // Trades
  'Plumbing', 'Electrical', 'HVAC & Ventilation', 'Carpentry & Joinery', 'Welding & Metalwork',
  // Finishing
  'Painting & Decorating', 'Plastering & Rendering', 'Tiling & Flooring', 'Ceiling Installation',
  // Specialized
  'Renovation & Remodeling', 'Waterproofing', 'Swimming Pools', 'Landscaping',
  'Interior Design', 'Kitchen & Bathroom Fitting',
  // Specialized Trades
  'Borehole Drilling', 'Solar Installation', 'Fencing & Gates',
  // Professional Services
  'Project Management', 'Quantity Surveying', 'Architectural Services', 'Engineering Consulting',
  // Extended Trades
  'Aluminium & Glazing', 'Ceiling & Partitioning', 'Paving & Driveways',
  'Borehole & Pump Servicing', 'Security & CCTV Installation', 'Solar & Inverter Systems',
  'Gate Automation', 'Steel Fabrication', 'Damp Proofing', 'Demolition',
  'Earthworks & Excavation', 'Road & Civil Works', 'Pre-cast Walling', 'Cabinetry & Fitted Furniture',
];

const VERIFIER_SPECIALIZATIONS = [
  'Residential', 'Commercial', 'Structural', 'Roofing',
  'Electrical', 'Plumbing', 'Masonry', 'Drainage',
  'HVAC', 'Waterproofing', 'Foundations', 'Interior Fit-out',
];

/** Tooltip descriptions for verifier specialization chips */
const VERIFIER_SPEC_TOOLTIPS = {
  'Residential':     'Houses, townhouses, flats, and other residential builds',
  'Commercial':      'Office blocks, retail spaces, and commercial properties',
  'Structural':      'Load-bearing walls, beams, columns, and structural integrity',
  'Roofing':         'Roof framing, tiling, waterproofing, and guttering',
  'Electrical':      'Wiring, distribution boards, lighting, and compliance',
  'Plumbing':        'Piping, fixtures, water supply, and drainage systems',
  'Masonry':         'Brickwork, blockwork, stone cladding, and mortar joints',
  'Drainage':        'Stormwater, sewage systems, and site drainage',
  'HVAC':            'Heating, ventilation, air conditioning, and ductwork',
  'Waterproofing':   'Damp-proofing, membrane installation, and moisture barriers',
  'Foundations':     'Footings, slabs, piling, and substructure work',
  'Interior Fit-out': 'Partitions, ceilings, flooring, and internal finishes',
};

const PRIMARY_TRADES = [
  'General Builder', 'Plumber', 'Electrician', 'Roofer', 'Painter',
  'Carpenter', 'Welder', 'Tiler', 'Mason', 'Plasterer',
  'Quantity Surveyor', 'Civil Engineer', 'Architect',
  'Project Manager', 'Landscaper', 'HVAC Technician',
];

const LICENSE_TYPES = [
  'Civil Engineer', 'Structural Engineer', 'Architect',
  'Quantity Surveyor', 'Building Inspector',
  'Quality Assurance Engineer', 'Project Manager',
];

// ─── ROLE CONFIG ──────────────────────────────────────────────────────────────

const ROLE_CONFIG = {
  contractor: {
    skipTier: false,
    tiers: [
      {
        id: 'individual',
        label: 'Individual Contractor',
        description: 'Solo contractor — browse projects, bid on work, build your reputation.',
        Icon: PersonOutline,
        capabilities: [
          'Browse project marketplace',
          'Submit bids & proposals',
          'Build a digital portfolio',
          'Receive payments directly',
        ],
      },
      {
        id: 'small_business',
        label: 'Small Business',
        description: 'Registered business with a small team. Access company profile features.',
        Icon: WorkspacePremium,
        capabilities: [
          'Company profile & business logo',
          'Team member management',
          'Business registration listing',
          'Access to larger projects',
        ],
      },
      {
        id: 'enterprise',
        label: 'Enterprise',
        description: 'Large construction company with full platform access and premium features.',
        Icon: Engineering,
        capabilities: [
          'Priority project matching',
          'Multiple teams & divisions',
          'Advanced analytics dashboard',
          'Dedicated account support',
        ],
      },
    ],
    specializations: CONTRACTOR_SPECIALIZATIONS,
    specializationsLabel: 'Your Specializations',
    hasPrimaryTrade: true,
    hasLicenseType: false,
    hasBackground: false,
    bioRole: 'contractor',
    photoLabel: {
      individual:    'Profile Photo',
      small_business: 'Business Logo',
      enterprise:    'Business Logo',
    },
    saveProfile: 'contractor',
    steps: ['Choose Tier', 'Basic Info', 'Specializations', 'Certifications', 'Photo & ID', 'Portfolio', 'Bio & Review'],
  },

  verifier: {
    skipTier: true,
    defaultTier: 'general',
    defaultVerificationStatus: 'pending',
    specializations: VERIFIER_SPECIALIZATIONS,
    specializationsLabel: 'Project Types You Verify',
    hasPrimaryTrade: false,
    hasLicenseType: false,
    hasBackground: false,
    bioRole: 'verifier',
    photoLabel: { individual: 'Profile Photo' },
    saveProfile: 'verifier',
    steps: ['Basic Info', 'Specializations', 'Certifications', 'Photo & ID', 'Bio & Review'],
  },

  professional_verifier: {
    skipTier: true,
    defaultTier: 'professional',
    defaultVerificationStatus: 'provisional',
    specializations: VERIFIER_SPECIALIZATIONS,
    specializationsLabel: 'Project Types You Verify',
    hasPrimaryTrade: false,
    hasLicenseType: true,
    hasBackground: true,
    bioRole: 'verifier',
    photoLabel: { individual: 'Profile Photo' },
    saveProfile: 'verifier',
    steps: ['Basic Info', 'Specializations', 'Certifications', 'Photo & ID', 'Bio & Review'],
  },

  quality_inspector: {
    skipTier: true,
    defaultTier: 'inspector',
    defaultVerificationStatus: 'pending',
    specializations: VERIFIER_SPECIALIZATIONS,
    specializationsLabel: 'Inspection Specializations',
    hasPrimaryTrade: false,
    hasLicenseType: true,
    hasBackground: true,
    bioRole: 'quality_inspector',
    photoLabel: { individual: 'Profile Photo' },
    saveProfile: 'inspector',
    steps: ['Basic Info', 'Specializations', 'Certifications', 'Photo & ID', 'Bio & Review'],
  },
};

// ─── REVIEW SECTION (presentational, defined outside component) ───────────────

const ReviewSection = ({ title, stepIndex, onEdit, children }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
      <Typography
        variant="caption"
        sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.6, color: 'text.secondary' }}
      >
        {title}
      </Typography>
      <Button
        size="small"
        startIcon={<Edit sx={{ fontSize: '0.7rem !important' }} />}
        onClick={() => onEdit(stepIndex)}
        sx={{
          color: TEAL, minWidth: 0, px: 0.75, py: 0.25,
          fontSize: '0.7rem', lineHeight: 1, ml: 0.5,
        }}
      >
        Edit
      </Button>
    </Box>
    {children}
  </Paper>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

const UnifiedOnboardingModal = ({ userRole, onComplete, variant = 'modal' }) => {
  const config   = ROLE_CONFIG[userRole] || ROLE_CONFIG.contractor;
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  // Roles that run the full-page APPLICATION flow (autosave/resume + submit to a
  // status page). Contractors and the verifier-type roles share this behaviour.
  const APPLICANT_PAGE = variant === 'page'
    && ['contractor', 'verifier', 'professional_verifier', 'quality_inspector'].includes(userRole);

  // Contractor uses brand primary; all other roles keep teal (#43B581)
  const PRIMARY       = userRole === 'contractor' ? brand.primary : TEAL;
  const PRIMARY_DARK  = userRole === 'contractor' ? brand.primaryDark : TEAL_DARK;
  const PRIMARY_LIGHT = userRole === 'contractor' ? '#e8f8f0' : TEAL_LIGHT;

  const [activeStep,     setActiveStep]     = useState(0);
  const [restored,       setRestored]       = useState(false);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState('');
  const [bioGenerating,  setBioGenerating]  = useState(false);
  const [bioGenerated,   setBioGenerated]   = useState(false);
  const [customSpecInput, setCustomSpecInput] = useState('');
  const [certInput,      setCertInput]      = useState({ name: '', issuing_org: '', date: '' });
  const [certModalOpen,  setCertModalOpen]  = useState(false);
  const [areaInput,      setAreaInput]      = useState('');
  const [profileLoaded,  setProfileLoaded]  = useState(false);
  const [hasExistingIdentityDocument, setHasExistingIdentityDocument] = useState(false);
  const [kycWaived, setKycWaived] = useState(false);
  // Counts reported by the Portfolio step (contractor only). Used to gate
  // Next/Submit: needs >=1 portfolio project (each with >=1 photo + a contactable
  // client, enforced inside the step modal). References are folded into the
  // client contact on each project, so there is no separate references count.
  const [portfolioRefCounts, setPortfolioRefCounts] = useState({ portfolioCount: 0, referencesCount: 0 });

  // Whether this user's mandatory documents are waived (global or per-user), so
  // the wizard can let them finish without uploading them.
  useEffect(() => {
    api.get(`/onboarding/document-requirements?role=${config.saveProfile === 'contractor' ? 'contractor' : userRole}`)
      .then((r) => setKycWaived(Boolean(r.data?.data?.waived)))
      .catch(() => {});
  }, [userRole, config.saveProfile]);

  const photoRef  = useRef(null);
  const idRef     = useRef(null);
  const bizRegRef = useRef(null);
  const taxRef    = useRef(null);
  const portfolioStepRef = useRef(null);

  const [formData, setFormData] = useState({
    // Tier / account type (contractor: set via tier card; others: fixed at config defaults)
    tier:         config.skipTier ? config.defaultTier : '',
    account_type: config.skipTier ? 'individual' : '',

    // Basic info
    first_name:          '',
    last_name:           '',
    phone:               '',
    city:                '',          // base city / HQ (kept for back-compat; synced to district)
    province:            '',
    district:            '',
    suburb:              '',
    address:             '',
    addressLocation:     null,        // structured business address (LocationInput)
    service_areas:       [],          // areas serviced (tag list)
    nationwide:          false,       // "Nationwide" checkbox
    background:          '',  // professional background — verifier/inspector only
    company_name:        '',
    registration_number: '',

    // Specializations step
    specializations: [],
    primary_trade:   '',   // contractor only
    license_type:    '',   // verifier/inspector only

    // Experience & certifications step
    years_experience: '',
    certifications:   [],  // [{ name, issuing_org, date }]

    // Photo & ID step
    profile_photo_file: null,   // Business Logo (company logo or contractor's face)
    profile_photo_url:  '',
    id_document_file:   null,   // Photo ID (director_id)
    business_reg_file:  null,   // Certificate of Incorporation (company_registration)
    tax_clearance_file: null,

    // Bio & Review step
    bio: '',
  });

  const steps     = config.steps;
  const isLastStep = activeStep === steps.length - 1;

  // ── DERIVED HELPERS ──────────────────────────────────────────────────────────

  /** DB account_type: 'individual' or 'company' (contractor tiers map here) */
  const dbAccountType = () =>
    !formData.account_type || formData.account_type === 'individual'
      ? 'individual'
      : 'company';

  /** Resolved photo label for the current tier/account combo */
  const resolvedPhotoLabel = () => {
    const key = formData.account_type === 'company'
      ? (formData.tier || 'individual')
      : 'individual';
    return config.photoLabel[key] || config.photoLabel['individual'] || 'Profile Photo';
  };

  // ── STATE HELPERS ─────────────────────────────────────────────────────────────

  const set = (field, value) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const handleTierSelect = (tierId) =>
    setFormData(prev => ({
      ...prev,
      tier:         tierId,
      account_type: tierId === 'individual' ? 'individual' : 'company',
    }));

  // ── NAVIGATION ────────────────────────────────────────────────────────────────

  const handleNext = async () => {
    // On the Portfolio step, make sure at least one project has been added before
    // advancing. Projects are saved from the modal, so there's nothing to flush.
    if (steps[activeStep] === 'Portfolio' && config.saveProfile === 'contractor') {
      setError('');
      let counts = portfolioRefCounts;
      if (portfolioStepRef.current?.flushPending) {
        try {
          counts = await portfolioStepRef.current.flushPending();
        } catch (e) {
          setError('Could not save your last entry. Please check it and try again.');
          return;
        }
      }
      if ((counts?.portfolioCount ?? 0) < 1) {
        setError('Add at least one portfolio project before continuing.');
        return;
      }
      setActiveStep(s => s + 1);
      return;
    }

    if (!validateStep(activeStep)) return;
    setError('');
    setActiveStep(s => s + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep(s => s - 1);
  };

  /** Used by Review section Edit buttons to jump directly to a step */
  const jumpToStep = (idx) => {
    setError('');
    setActiveStep(idx);
  };

  // ── BIO GENERATION ────────────────────────────────────────────────────────────

  // Prefill locked fields from the registration profile, then (for the contractor
  // application) restore any saved draft so they resume exactly where they left off.
  useEffect(() => {
    const isApplicationFlow = APPLICANT_PAGE;
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get('/auth/users/profile');
        if (!cancelled && resp.data.success) {
          const p = resp.data.data;
          // Coerce jsonb/text[] columns that may come back as a string or array.
          const asArray = (v) => {
            if (Array.isArray(v)) return v;
            if (typeof v === 'string' && v.trim()) {
              try { const a = JSON.parse(v); return Array.isArray(a) ? a : []; } catch { return []; }
            }
            return [];
          };
          const savedAreas = asArray(p.service_areas);
          const nationwide = savedAreas.some(a => String(a).toLowerCase() === 'nationwide');
          const savedSpecs = asArray(p.specializations);
          // Rehydrate the whole submitted application from the saved profile, so a
          // returning applicant sees everything they entered even if the autosaved
          // draft was lost. The saved draft (restored just below) still wins where
          // it has a value, since it reflects in-progress edits.
          setFormData(prev => ({
            ...prev,
            first_name:          p.first_name          || prev.first_name          || '',
            last_name:           p.last_name           || prev.last_name           || '',
            phone:               p.phone_number        || prev.phone               || '',
            company_name:        p.company_name        || prev.company_name        || '',
            registration_number: p.registration_number || prev.registration_number || '',
            profile_photo_url:   p.profile_image_url   || prev.profile_photo_url   || '',
            province:            p.province            || prev.province            || '',
            district:            p.district            || prev.district            || '',
            suburb:              p.suburb              || prev.suburb              || '',
            address:             p.address             || prev.address             || '',
            // `city` is a legacy mirror of district ("Area you cover"); backfill
            // from district so a saved profile without a city doesn't dead-end
            // the (hidden-field) "City is required" validation on resume.
            city:                p.city                || p.district || prev.city || '',
            addressLocation:     prev.addressLocation || (p.address
              ? { address: p.address, city: p.city || '', province: p.province || '', suburb: p.suburb || '' }
              : null),
            specializations:     savedSpecs.length ? savedSpecs : prev.specializations,
            service_areas:       nationwide ? [] : (savedAreas.length ? savedAreas : prev.service_areas),
            nationwide:          nationwide || prev.nationwide,
            years_experience:    (p.years_experience !== null && p.years_experience !== undefined && p.years_experience !== '')
              ? String(p.years_experience) : prev.years_experience,
            bio:                 p.bio || prev.bio || '',
            primary_trade:       p.primary_trade || prev.primary_trade || '',
            account_type:        p.account_type || prev.account_type,
            certifications:      (prev.certifications && prev.certifications.length)
              ? prev.certifications
              : (p.certifications_text
                  ? String(p.certifications_text).split(',').map(s => s.trim()).filter(Boolean).map(name => ({ name, issuing_org: '', date: '' }))
                  : prev.certifications),
            ...(p.suggested_tier ? { tier: p.suggested_tier } : {}),
          }));
        }
      } catch (_) { /* non-critical — proceed without pre-fill */ }

      if (isApplicationFlow) {
        try {
          const d = await api.get('/contractors-onboarding/application/draft');
          if (!cancelled && d.data?.success) {
            const { step, form } = d.data.data || {};
            if (form && Object.keys(form).length) {
              setFormData(prev => ({ ...prev, ...form }));
            }
            if (Number.isInteger(step) && step > 0 && step < steps.length) {
              setActiveStep(step);
            }
          }
        } catch (e) {
          // An unverified (pending) account 403s on every application endpoint —
          // say so instead of silently losing autosave/uploads.
          if (!cancelled && e?.response?.data?.code === 'ACCOUNT_PENDING') {
            setError('Please verify your email address first — check your inbox for the verification code. Until then your application progress cannot be saved.');
          }
          /* otherwise: no saved draft — start fresh */
        }
      }

      if (!cancelled) { setProfileLoaded(true); setRestored(true); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced auto-save of the in-progress application (step + serializable form
  // fields) so the contractor can leave and return to the same place. File inputs
  // can't be serialized and are stripped; uploaded files already persist server-side.
  useEffect(() => {
    if (!APPLICANT_PAGE || !restored) return undefined;
    const form = Object.fromEntries(
      Object.entries(formData).filter(([k, v]) => !k.endsWith('_file') && !(v instanceof File))
    );
    const t = setTimeout(() => {
      api.patch('/contractors-onboarding/application/draft', { step: activeStep, form })
        .catch(() => { /* autosave is best-effort */ });
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, formData, restored]);

  // On the application flow, detect whether the applicant already uploaded an
  // identity document so the Photo & ID step shows "already uploaded" and doesn't
  // force a re-upload (file inputs can't be restored). Covers contractor (ID is
  // saved as director_id) and verifier-type roles (national_id/passport/license).
  useEffect(() => {
    if (!APPLICANT_PAGE) return;
    const IDENTITY_TYPES = ['director_id', 'national_id', 'id_card', 'passport', 'license'];
    api.get('/contractors-onboarding/application/documents')
      .then((r) => {
        const docs = r.data?.data || [];
        if (docs.some(d => IDENTITY_TYPES.includes(d.document_type))) {
          setHasExistingIdentityDocument(true);
        }
      })
      .catch(() => { /* non-critical — wizard will ask for the ID if unsure */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (config.saveProfile !== 'verifier') return;

    api.get('/verifiers/profile/me')
      .then(async resp => {
        const profile = resp.data?.data;
        if (!profile?.id) return;

        try {
          const docsResp = await api.get(`/verifiers/profile/${profile.id}/documents`);
          const docs = docsResp.data?.data || [];
          setHasExistingIdentityDocument(docs.some(doc =>
            doc.document_category === 'identity' &&
            ['national_id', 'license', 'passport'].includes(doc.document_type)
          ));
        } catch (_) {
          // Non-critical; validation will ask for an ID upload if we cannot confirm one.
        }
      })
      .catch(() => {
        // New verifier profile; ID will be collected during this wizard.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-trigger bio generation once when the Bio & Review step first mounts
  useEffect(() => {
    if (steps[activeStep] === 'Bio & Review' && !bioGenerated && !bioGenerating) {
      triggerBioGeneration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  const triggerBioGeneration = async () => {
    setBioGenerating(true);
    try {
      const resp = await api.post('/onboarding/generate-bio', {
        role:             config.bioRole,
        primary_trade:    formData.primary_trade || formData.background || '',
        background:       formData.background    || formData.primary_trade || '',
        years_experience: formData.years_experience || '',
        specializations:  formData.specializations.join(', '),
        certifications:   formData.certifications.map(c => c.name).join(', '),
        city:             formData.city || '',
        service_areas:    formData.nationwide ? 'Nationwide' : formData.service_areas.join(', '),
        account_type:     formData.account_type || 'individual',
        company_name:     formData.company_name  || '',
      });
      if (resp.data.success) {
        set('bio', resp.data.data.bio);
        setBioGenerated(true);
        return;
      }
    } catch { /* fall through to fallback */ }

    // Fallback bio built from form data
    const trade   = formData.primary_trade || formData.background || 'construction professional';
    const years   = formData.years_experience || '5';
    const city    = formData.city || 'Zimbabwe';
    const topSpec = formData.specializations.slice(0, 3).join(', ') || 'construction services';
    let fallback  = '';

    if (config.bioRole === 'contractor') {
      fallback = `Trusted ${trade} with ${years}+ years of experience, based in ${city}. ` +
        `Specializing in ${topSpec}. Known for quality workmanship, on-time delivery, ` +
        `and transparent communication. Contact today to discuss your project.`;
    } else {
      const roleLabel = config.bioRole === 'quality_inspector' ? 'quality inspector' : 'verifier';
      fallback = `Independent ${trade} with ${years}+ years of experience providing ` +
        `professional ${roleLabel} services in ${city}. ` +
        `Specializing in ${topSpec}. Available for site visits and milestone inspections. ` +
        `Contact for scheduling.`;
    }

    set('bio', fallback);
    setBioGenerated(true);
    setBioGenerating(false);
  };

  // ── VALIDATION ────────────────────────────────────────────────────────────────

  const validateStep = (stepIdx) => {
    const name = steps[stepIdx];

    if (name === 'Choose Tier') {
      if (!formData.tier) {
        setError('Please select a contractor type to continue.');
        return false;
      }
    }

    if (name === 'Basic Info') {
      const isContractorProfile = config.saveProfile === 'contractor';
      // Business name + service areas are contractor-specific; verifiers are
      // individuals and don't provide them. Phone + province/district/city are
      // mandatory for every applicant (contractor and verifier checklists alike).
      if (isContractorProfile && !formData.company_name?.trim()) { setError('Business name is required.'); return false; }
      if (!formData.phone?.trim())        { setError('Phone number is required.'); return false; }
      // Street address comes from the lookup and is required for every applicant
      // (contractors and verifiers alike must give us their physical address).
      if (!(formData.address || '').trim()) {
        setError(isContractorProfile ? 'Business address is required.' : 'Your physical address is required.');
        return false;
      }
      if (!formData.province) { setError('Please select your province.'); return false; }
      if (!formData.district) { setError('Please select your district.'); return false; }
      // `city` is a legacy mirror of the district above and has no visible field.
      // It only auto-syncs when the user actively edits district, so a resumed /
      // address-lookup-filled district can leave it blank and dead-end the user.
      // District is already required above — backfill city from it, never block.
      if (!(formData.city || '').trim()) {
        setFormData(prev => ({ ...prev, city: prev.district }));
      }
      if (isContractorProfile && !formData.nationwide && formData.service_areas.length === 0) {
        setError('Please add at least one service area, or check "I can travel anywhere / Nationwide".');
        return false;
      }
      // first_name/last_name are pre-filled for individual; only validate when company (editable)
      if (formData.account_type === 'company') {
        if (!formData.first_name.trim()) { setError('First name is required.'); return false; }
        if (!formData.last_name.trim())  { setError('Last name is required.'); return false; }
      }
    }

    if (name === 'Specializations') {
      // Match the backend completion contract (ProfileCompletionService):
      // contractors need >= 3 specializations, others >= 1. Without this the
      // wizard let contractors finish with 1 and then sit stuck below 100%.
      const minSpecs = minSpecializations(userRole);
      if (formData.specializations.length < minSpecs) {
        setError(minSpecs > 1
          ? `Please select at least ${minSpecs} specializations.`
          : 'Please select at least one specialization.');
        return false;
      }
    }

    if (name === 'Certifications') {
      const yrs = parseInt(formData.years_experience, 10);
      if (!formData.years_experience || isNaN(yrs) || yrs < 0) {
        setError('Please enter a valid number for years of experience.');
        return false;
      }
    }

    if (name === 'Photo & ID') {
      // Verifiers and quality inspectors must have a profile photo and a
      // government ID on file (both are required by the verifier checklist).
      if (['verifier', 'inspector'].includes(config.saveProfile)) {
        if (!formData.profile_photo_file && !formData.profile_photo_url) {
          setError('Profile photo is required.');
          return false;
        }
        if (!formData.id_document_file && !hasExistingIdentityDocument) {
          setError('Please upload a National ID, passport, or professional license to finish your setup.');
          return false;
        }
      }
      // Contractors must provide the mandatory KYC documents, unless an admin
      // has waived them (globally or for this contractor). The Business Logo is
      // optional — it only enhances the public contractor card.
      if (config.saveProfile === 'contractor') {
        if (!kycWaived) {
          // Government-issued Photo ID is the only mandatory document. An ID
          // uploaded on a previous visit (hasExistingIdentityDocument) counts.
          if (!formData.id_document_file && !hasExistingIdentityDocument) {
            setError('Photo ID is required to verify your identity.');
            return false;
          }
          // Certificate of Incorporation and Tax clearance are optional and
          // never block advancing (individuals/sole traders may not have them).
        }
      }
    }

    if (name === 'Portfolio' && config.saveProfile === 'contractor') {
      if (portfolioRefCounts.portfolioCount < 1) {
        setError('Add at least one portfolio project before continuing.');
        return false;
      }
    }

    return true;
  };

  // ── FILE HANDLERS ─────────────────────────────────────────────────────────────

  const handlePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please upload a JPG or PNG image.'); return; }
    if (file.size > 5 * 1024 * 1024)    { setError('Photo must be under 5 MB.'); return; }
    set('profile_photo_file', file);
    setError('');
    // Persist immediately: files can't be saved in the draft, so without this
    // a page reload silently loses the photo. The URL IS drafted, so once
    // uploaded it survives reloads and the final submit reuses it.
    try {
      const fd = new FormData();
      fd.append('image', file);
      const r = await api.post('/uploads/profile-picture', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (r.data.success && r.data.data?.url) {
        setFormData(prev => ({ ...prev, profile_photo_url: r.data.data.url, profile_photo_file: null }));
      }
    } catch (_) { /* keep the file in memory — the final submit retries the upload */ }
  };

  const handleIdSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      setError('Please upload an image or PDF file.'); return;
    }
    if (file.size > 10 * 1024 * 1024) { setError('Document must be under 10 MB.'); return; }
    set('id_document_file', file);
    setError('');
    // Persist immediately (same reason as the photo): the ID lands in
    // user_documents now, so a reload shows "ID on file" instead of losing it.
    try {
      const isContractor = config.saveProfile === 'contractor';
      const fd = new FormData();
      fd.append('file', file);
      fd.append('documentType', isContractor ? 'director_id' : 'national_id');
      fd.append('documentName', isContractor ? 'Owner/Director ID' : 'Government ID');
      await api.post('/uploads/documents', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setHasExistingIdentityDocument(true);
      set('id_document_file', null); // uploaded — the submit step won't re-upload
    } catch (_) { /* keep the file in memory — the final submit retries the upload */ }
  };

  const handleBizRegSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      setError('Please upload an image or PDF file.'); return;
    }
    if (file.size > 10 * 1024 * 1024) { setError('Document must be under 10 MB.'); return; }
    set('business_reg_file', file);
    setError('');
  };

  const handleTaxClearanceSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      setError('Please upload an image or PDF file.'); return;
    }
    if (file.size > 10 * 1024 * 1024) { setError('Document must be under 10 MB.'); return; }
    set('tax_clearance_file', file);
    setError('');
  };

  // ── SAVE SEQUENCE ─────────────────────────────────────────────────────────────

  const handleComplete = async () => {
    const hasBioStep = steps.includes('Bio & Review');
    // Flows without a Bio & Review step get an auto-generated fallback bio
    if (!formData.bio.trim()) {
      if (hasBioStep) {
        setError('Please wait for bio generation to complete, or type your bio.');
        return;
      }
      // Generate a quick fallback bio for shortened flows (e.g. verifier 3-step)
      const roleLabel = config.bioRole === 'quality_inspector' ? 'quality inspector' : config.bioRole;
      const city = formData.city || 'Zimbabwe';
      const topSpec = formData.specializations.slice(0, 3).join(', ') || 'construction verification';
      set('bio', `Independent ${roleLabel} based in ${city}, specializing in ${topSpec}. Available for site visits and milestone verifications.`);
      // Update formData directly for the save below
      formData.bio = `Independent ${roleLabel} based in ${city}, specializing in ${topSpec}. Available for site visits and milestone verifications.`;
    }
    setSaving(true);
    setError('');

    const uploadWarnings = [];

    try {
      // 1. Upload profile photo
      let photoUrl = formData.profile_photo_url || null;
      if (formData.profile_photo_file) {
        try {
          const fd = new FormData();
          fd.append('image', formData.profile_photo_file);
          const r = await api.post('/uploads/profile-picture', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          if (r.data.success) photoUrl = r.data.data.url;
        } catch (e) {
          console.warn('Photo upload failed:', e.message);
          uploadWarnings.push('Profile photo could not be uploaded. You can add it later from your profile page.');
        }
      }

      // 2. Upload ID document for non-verifier flows. Verifier profiles need
      // the profile ID first so the document is registered in verifier_documents.
      if (formData.id_document_file && config.saveProfile !== 'verifier') {
        try {
          // For contractors the ID is the owner/director ID — save it with the
          // type the KYC checklist actually requires (director_id), not id_card.
          const isContractor = config.saveProfile === 'contractor';
          const fd = new FormData();
          fd.append('file', formData.id_document_file);
          fd.append('documentType', isContractor ? 'director_id' : 'national_id');
          fd.append('documentName', isContractor ? 'Owner/Director ID' : 'National ID');
          await api.post('/uploads/documents', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (e) {
          console.warn('ID upload failed:', e.message);
          uploadWarnings.push('ID document could not be uploaded. You can add it later from your profile page.');
        }
      }

      // 3. Upload company / business registration (all contractor accounts)
      if (formData.business_reg_file) {
        try {
          const fd = new FormData();
          fd.append('file', formData.business_reg_file);
          fd.append('documentType', 'company_registration');
          fd.append('documentName', 'Company registration');
          await api.post('/uploads/documents', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (e) {
          console.warn('Company registration upload failed:', e.message);
          uploadWarnings.push('Company registration could not be uploaded. You can add it later from your profile page.');
        }
      }

      // 3b. Upload tax clearance (optional)
      if (formData.tax_clearance_file) {
        try {
          const fd = new FormData();
          fd.append('file', formData.tax_clearance_file);
          fd.append('documentType', 'tax_clearance');
          fd.append('documentName', 'Tax clearance');
          await api.post('/uploads/documents', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (e) {
          console.warn('Tax clearance upload failed:', e.message);
          uploadWarnings.push('Tax clearance could not be uploaded. You can add it later from your profile page.');
        }
      }

      // 4. Save profile data per role
      let savedProfile = null;
      if (config.saveProfile === 'contractor') savedProfile = await saveContractor(photoUrl);
      else if (config.saveProfile === 'verifier') savedProfile = await saveVerifier(photoUrl);
      else if (config.saveProfile === 'inspector') savedProfile = await saveInspector(photoUrl);

      // 4b. Register verifier identity documents against verifier_documents.
      if (config.saveProfile === 'verifier' && formData.id_document_file && savedProfile?.id) {
        try {
          const fd = new FormData();
          fd.append('file', formData.id_document_file);
          fd.append('verifierProfileId', savedProfile.id);
          fd.append('documentType', 'national_id');
          fd.append('documentCategory', 'identity');
          fd.append('documentName', 'National ID');
          await api.post('/uploads/documents', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (e) {
          console.warn('Verifier ID upload failed:', e.message);
          uploadWarnings.push('Identity document could not be uploaded. You can add it later from your profile page.');
        }
      }

      // 4b-ii. Ensure the verifier's government ID is also on user_documents — the
      // application checklist and the accept-work gate both read that table. The
      // 4b upload above already persists it there via persistUserDocument, but this
      // belt-and-braces upload (no verifierProfileId) guarantees it for every
      // verifier-type role regardless of which save path ran.
      if (['verifier', 'professional_verifier', 'quality_inspector'].includes(userRole) && formData.id_document_file) {
        try {
          const fd = new FormData();
          fd.append('file', formData.id_document_file);
          fd.append('documentType', 'national_id');
          fd.append('documentName', 'Government ID');
          await api.post('/uploads/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (e) { console.warn('verifier user_documents ID upload failed:', e.message); }
      }

      // 4c. Contractor portfolio projects are persisted by the Portfolio step as
      // the user saves each one (writing to contractor_portfolio — the table the
      // application checklist reads), so nothing to save here.

      // 5. Mark onboarding complete
      try {
        await api.post('/onboarding/save-progress', {
          phase: 2,
          data: { completedAt: new Date().toISOString() },
        });
      } catch (e) { console.warn('Progress save failed (non-critical):', e.message); }

      // 6. Applicants (contractor + verifier-type roles) submit their application
      // for review against the role-aware checklist. On success, route to the
      // right status page. On 422 (incomplete), surface the missing items and keep
      // them on the wizard. This runs AFTER the profile + ID are persisted above.
      const VERIFIER_ROLES = ['verifier', 'professional_verifier', 'quality_inspector'];
      const isApplicant = userRole === 'contractor' || VERIFIER_ROLES.includes(userRole);
      if (isApplicant && variant === 'page') {
        try {
          await api.post('/contractors-onboarding/application/submit');
          navigate(userRole === 'contractor' ? '/contractor/application-status' : '/verifier/application-status');
          return;
        } catch (e) {
          if (e?.response?.status === 422) {
            const missing = e.response.data?.missing || [];
            setError('Please complete all required items before submitting: ' + missing.join(', '));
            return; // keep them on the wizard
          }
          setError('Could not submit your application. Please try again.');
          return;
        }
      }

      // If opened from profile page, call onComplete to refetch; otherwise navigate to dashboard
      if (onComplete) {
        onComplete();
      } else {
        const hasWarnings = uploadWarnings.length > 0;
        const welcomeTarget = userRole === 'contractor' ? '/contractor/welcome'
          : userRole === 'verifier' ? '/verifier/welcome'
          : '/dashboard';
        navigate(welcomeTarget, {
          state: {
            message: hasWarnings
              ? `Profile created, but some uploads failed: ${uploadWarnings.join(' ')}`
              : `Welcome to Eqaya! Your ${userRole.replace(/_/g, ' ')} profile has been created.`,
            type: hasWarnings ? 'warning' : 'success',
            showTour: true,
          },
        });
      }
    } catch (err) {
      console.error('Onboarding save error:', err);
      setError(
        err.response?.data?.message ||
        err.message ||
        'Failed to save profile. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  // Contractor: PUT /auth/users/profile + insert contractor_certifications
  const saveContractor = async (photoUrl) => {
    await api.put('/auth/users/profile', {
      first_name:           formData.first_name,
      last_name:            formData.last_name,
      phone_number:         formData.phone || null,
      city:                 formData.city,
      province:             formData.province,
      district:             formData.district,
      suburb:               formData.suburb,
      address:              formData.address,
      service_areas:        formData.nationwide ? ['Nationwide'] : formData.service_areas,
      bio:                  formData.bio,
      years_experience:     parseInt(formData.years_experience, 10) || 0,
      primary_trade:        formData.primary_trade   || null,
      account_type:         dbAccountType(),
      company_name:         formData.company_name    || null,
      registration_number:  formData.registration_number || null,
      specializations:      formData.specializations,
      certifications_text:  formData.certifications.map(c => c.name).join(', '),
      suggested_tier:       formData.tier || 'individual',
      ...(photoUrl ? { profile_image_url: photoUrl } : {}),
    });

    // Insert structured certifications (non-critical)
    for (const cert of formData.certifications) {
      try {
        await api.post('/contractors/certifications', {
          certification_name:    cert.name,
          issuing_organization:  cert.issuing_org || null,
          issue_date:            cert.date         || null,
        });
      } catch (e) {
        console.warn(`Cert insert "${cert.name}" failed (non-critical):`, e.message);
      }
    }

    return null;
  };

  // Verifier: create or update /verifiers/profile + sync user_profiles
  const saveVerifier = async (photoUrl) => {
    const payload = {
      tier:                 config.defaultTier,
      bio:                  formData.bio,
      base_city:            formData.city,
      service_areas:        formData.nationwide ? ['Nationwide'] : formData.service_areas,
      years_experience:     parseInt(formData.years_experience, 10) || 0,
      specializations:      formData.specializations,
      certifications:       formData.certifications.map(c => ({
        name:   c.name,
        issuer: c.issuing_org || null,
        date:   c.date        || null,
      })),
      education:            formData.background   || null,
      license_type:         formData.license_type || null,
      is_available:         true,
      verification_status:  config.defaultVerificationStatus || 'pending',
      account_type:         'individual',
      ...(photoUrl ? { profile_image_url: photoUrl } : {}),
    };

    let savedProfile = null;
    try {
      const existing = await api.get('/verifiers/profile/me');
      if (existing.data.success && existing.data.data?.id) {
        const existingProfile = existing.data.data;
        const response = await api.put(`/verifiers/profile/${existingProfile.id}`, {
          ...payload,
          tier: existingProfile.tier || payload.tier,
          verification_status: existingProfile.verification_status || payload.verification_status,
        });
        savedProfile = response.data.data;
      }
    } catch (_) {
      // No existing profile; create below.
    }

    if (!savedProfile) {
      const response = await api.post('/verifiers/profile', payload);
      savedProfile = response.data.data;
    }

    // Sync basic fields to user_profiles
    try {
      await api.put('/auth/users/profile', {
        first_name:   formData.first_name,
        last_name:    formData.last_name,
        phone_number: formData.phone || null,
        city:         formData.city,
        province:     formData.province,
        district:     formData.district,
        suburb:       formData.suburb,
        address:      formData.address,
        bio:          formData.bio,
        ...(photoUrl ? { profile_image_url: photoUrl } : {}),
      });
    } catch (e) { console.warn('user_profiles sync failed (non-critical):', e.message); }

    return savedProfile;
  };

  // Inspector: POST /inspectors/profile + sync user_profiles
  const saveInspector = async (photoUrl) => {
    await api.post('/inspectors/profile', {
      tier:                 config.defaultTier,
      bio:                  formData.bio,
      base_city:            formData.city,
      service_areas:        formData.nationwide ? ['Nationwide'] : formData.service_areas,
      years_experience:     parseInt(formData.years_experience, 10) || 0,
      specializations:      formData.specializations,
      certifications:       formData.certifications.map(c => ({
        name:   c.name,
        issuer: c.issuing_org || null,
        date:   c.date        || null,
      })),
      education:            formData.background   || null,
      license_type:         formData.license_type || null,
      is_available:         true,
      verification_status:  config.defaultVerificationStatus || 'pending',
      account_type:         'inspector',
      ...(photoUrl ? { profile_image_url: photoUrl } : {}),
    });

    try {
      await api.put('/auth/users/profile', {
        first_name:   formData.first_name,
        last_name:    formData.last_name,
        phone_number: formData.phone || null,
        city:         formData.city,
        province:     formData.province,
        district:     formData.district,
        suburb:       formData.suburb,
        address:      formData.address,
        bio:          formData.bio,
        ...(photoUrl ? { profile_image_url: photoUrl } : {}),
      });
    } catch (e) { console.warn('user_profiles sync failed (non-critical):', e.message); }
  };

  // ─── STEP 0: CHOOSE TIER ─────────────────────────────────────────────────────

  const renderChooseTier = () => (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Choose Your Contractor Type
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select the type that best describes your operation. This sets your profile features.
      </Typography>

      <Grid container spacing={2}>
        {config.tiers.map(({ id, label, description, Icon, capabilities }) => {
          const selected = formData.tier === id;
          return (
            <Grid item xs={12} md={4} key={id}>
              <Card
                onClick={() => handleTierSelect(id)}
                sx={{
                  cursor: 'pointer',
                  border: 2,
                  borderColor: selected ? TEAL : 'divider',
                  bgcolor: selected ? TEAL_LIGHT : 'background.paper',
                  height: '100%',
                  transition: 'border-color 0.15s, background-color 0.15s',
                  '&:hover': { borderColor: TEAL },
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    {selected
                      ? <CheckCircle sx={{ color: TEAL, fontSize: 28, mr: 1 }} />
                      : <Icon sx={{ color: 'text.disabled', fontSize: 28, mr: 1 }} />
                    }
                    <Typography variant="subtitle1" fontWeight={700}>{label}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
                    {description}
                  </Typography>
                  {capabilities.map(cap => (
                    <Box key={cap} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                      <CheckCircle sx={{ fontSize: 12, color: TEAL, mr: 0.75, mt: 0.3, flexShrink: 0 }} />
                      <Typography variant="caption" lineHeight={1.4}>{cap}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  // ─── STEP: BASIC INFO ────────────────────────────────────────────────────────

  const renderBasicInfo = () => {
    const isCompany = formData.account_type === 'company';
    const isContractorProfile = config.saveProfile === 'contractor';
    const lockedSx  = { '& .MuiInputBase-root': { bgcolor: '#f5f5f5' } };
    const lockEnd   = { endAdornment: <Lock sx={{ fontSize: 16, color: 'action.disabled' }} /> };
    const lockedNote = 'From your account — change in Account Settings';

    return (
      <Box>
        <Typography variant="h6" fontWeight={700} gutterBottom>Basic Information</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Your personal and contact details.
        </Typography>

        <Grid container spacing={2}>
          {/* Email — always display-only (from auth context) */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email" fullWidth
              value={user?.email || ''}
              InputProps={{ readOnly: true, ...lockEnd }}
              sx={lockedSx}
              helperText="Your registered email address"
            />
          </Grid>

          {/* Business name — contractor-only (individuals enter a trading name;
              company accounts show the locked registration-sourced name below).
              Verifiers are individuals and don't provide a business name. */}
          {!isCompany && config.saveProfile === 'contractor' && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="Business name" fullWidth required
                value={formData.company_name}
                onChange={e => set('company_name', e.target.value)}
                helperText="Your registered business or trading name — shown on your contractor card."
              />
            </Grid>
          )}

          {/* Individual: locked name fields */}
          {!isCompany && (
            <>
              <Grid item xs={12} sm={6}>
                {/* spacer to push names to next row */}
                <Box sx={{ height: '100%' }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name" fullWidth
                  value={formData.first_name}
                  InputProps={{ readOnly: true, ...lockEnd }}
                  sx={lockedSx}
                  helperText={lockedNote}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name" fullWidth
                  value={formData.last_name}
                  InputProps={{ readOnly: true, ...lockEnd }}
                  sx={lockedSx}
                  helperText={lockedNote}
                />
              </Grid>
            </>
          )}

          {/* Company: editable contact name + locked company fields */}
          {isCompany && (
            <>
              <Grid item xs={12} sm={6}>
                <Box sx={{ height: '100%' }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name" fullWidth required
                  value={formData.first_name}
                  onChange={e => set('first_name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name" fullWidth required
                  value={formData.last_name}
                  onChange={e => set('last_name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Company Name" fullWidth required
                  value={formData.company_name}
                  onChange={e => set('company_name', e.target.value)}
                  helperText="Your registered business name — shown on your contractor card."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Registration Number" fullWidth
                  value={formData.registration_number}
                  onChange={e => set('registration_number', e.target.value)}
                  helperText="Company / business registration number (optional)."
                />
              </Grid>
            </>
          )}

          {/* Phone — always editable */}
          <Grid item xs={12} sm={6}>
            <PhoneField
              label="Phone Number" required
              value={formData.phone}
              onChange={e => set('phone', e.target.value)}
              helperText="Pick your country, then the number without the leading 0 — this is the number we reach you on"
            />
          </Grid>

          {/* Address — comes right after phone (it's part of basic info). The
              lookup captures the street address only; the province/district below
              define the area you cover. Same lookup the New Project flow uses. */}
          <Grid item xs={12}>
            <LocationInput
              label={isContractorProfile ? 'Business address' : 'Your address'}
              required
              value={formData.addressLocation || null}
              onChange={(loc) => setFormData((p) => ({
                ...p,
                addressLocation: loc,
                address: loc?.address || p.address,
              }))}
              helperText={isContractorProfile
                ? 'Where your business operates from. Same lookup the project flow uses.'
                : 'Where you are based. Same lookup the project flow uses.'}
            />
          </Grid>

          {/* Area you cover — province + district (the area you work in). Suburb
              and street line are dropped here; the address lookup above covers the
              street, and the area only needs province + district. */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Area you cover
            </Typography>
            <ZimAddressFields
              value={{ province: formData.province, district: formData.district }}
              show={['province', 'district']}
              required
              onChange={(next) => setFormData(prev => ({
                ...prev,
                province: next.province ?? prev.province,
                district: next.district ?? prev.district,
                // keep legacy `city` populated for downstream payloads + the summary chip
                city: next.district || prev.city,
              }))}
            />
          </Grid>

          {/* Willing to travel to other areas? — logically follows once they've
              said which province/district they cover. */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75 }}>
              Willing to travel to other areas?{isContractorProfile ? ' *' : ''}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.nationwide}
                  onChange={e => {
                    set('nationwide', e.target.checked);
                    if (e.target.checked) set('service_areas', []);
                  }}
                  sx={{ color: TEAL, '&.Mui-checked': { color: TEAL } }}
                  size="small"
                />
              }
              label={<Typography variant="body2">I can travel anywhere / Nationwide</Typography>}
              sx={{ mb: 1 }}
            />
            {!formData.nationwide && (
              <>
                {formData.service_areas.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                    {formData.service_areas.map(area => (
                      <Chip
                        key={area} label={area} size="small"
                        onDelete={() => set('service_areas', formData.service_areas.filter(a => a !== area))}
                        sx={{ bgcolor: TEAL_LIGHT, color: '#1a5c3a', '& .MuiChip-deleteIcon': { color: TEAL } }}
                      />
                    ))}
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    size="small" sx={{ flex: 1 }}
                    placeholder="Type a city or area and press Enter"
                    value={areaInput}
                    onChange={e => setAreaInput(e.target.value)}
                    onKeyPress={e => {
                      if (e.key === 'Enter' && areaInput.trim()) {
                        if (!formData.service_areas.includes(areaInput.trim())) {
                          set('service_areas', [...formData.service_areas, areaInput.trim()]);
                        }
                        setAreaInput('');
                      }
                    }}
                    helperText="Press Enter after each city/area"
                  />
                  <Button
                    variant="outlined" size="small"
                    onClick={() => {
                      if (areaInput.trim() && !formData.service_areas.includes(areaInput.trim())) {
                        set('service_areas', [...formData.service_areas, areaInput.trim()]);
                      }
                      setAreaInput('');
                    }}
                    disabled={!areaInput.trim()}
                    sx={{ borderColor: TEAL, color: TEAL, '&:hover': { borderColor: TEAL_DARK, bgcolor: TEAL_LIGHT }, mt: 0.25 }}
                  >
                    Add
                  </Button>
                </Box>
              </>
            )}
          </Grid>

          {/* Professional background — verifier / inspector only */}
          {config.hasBackground && (
            <Grid item xs={12}>
              <TextField
                label="Professional Background"
                fullWidth
                value={formData.background}
                onChange={e => set('background', e.target.value)}
                placeholder="e.g., Civil Engineer, Architect, Construction Manager"
                helperText="Your professional title or highest qualification"
              />
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  // ─── STEP: SPECIALIZATIONS ───────────────────────────────────────────────────

  const renderSpecializations = () => {
    const toggleSpec = (spec) => {
      const next = formData.specializations.includes(spec)
        ? formData.specializations.filter(s => s !== spec)
        : [...formData.specializations, spec];
      set('specializations', next);
    };

    return (
      <Box>
        {/* Primary Trade — contractor only, shown first (the trade you do most). */}
        {config.hasPrimaryTrade && (
          <>
            <Typography variant="h6" fontWeight={700} gutterBottom>Primary Trade</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Select the trade you specialize in most.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {PRIMARY_TRADES.map(trade => {
                const sel = formData.primary_trade === trade;
                return (
                  <Chip
                    key={trade} label={trade}
                    onClick={() => set('primary_trade', sel ? '' : trade)}
                    variant={sel ? 'filled' : 'outlined'}
                    sx={sel
                      ? { bgcolor: TEAL, color: '#fff', cursor: 'pointer', '&:hover': { bgcolor: TEAL_DARK } }
                      : { cursor: 'pointer', '&:hover': { bgcolor: TEAL_LIGHT } }
                    }
                  />
                );
              })}
            </Box>
            <Divider sx={{ mb: 2 }} />
          </>
        )}

        {config.hasPrimaryTrade ? (
          <>
            <Typography variant="h6" fontWeight={700} gutterBottom>Other specializations</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select all the trades that apply. You can also add custom options below.
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h6" fontWeight={700} gutterBottom>{config.specializationsLabel}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select all that apply. You can also add custom options below.
            </Typography>
          </>
        )}

        {/* Selected tags row */}
        {formData.specializations.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {formData.specializations.map(spec => (
              <Chip
                key={spec}
                label={spec}
                size="small"
                onDelete={() => toggleSpec(spec)}
                sx={{
                  bgcolor: TEAL_LIGHT,
                  color: '#1a5c3a',
                  '& .MuiChip-deleteIcon': { color: TEAL },
                }}
              />
            ))}
          </Box>
        )}

        {/* Chip grid */}
        <Box
          sx={{
            display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2,
            maxHeight: 240, overflowY: 'auto', py: 0.5, pr: 0.5,
          }}
        >
          {config.specializations.map(spec => {
            const selected = formData.specializations.includes(spec);
            const tooltip = VERIFIER_SPEC_TOOLTIPS[spec];
            const chip = (
              <Chip
                key={spec}
                label={spec}
                onClick={() => toggleSpec(spec)}
                variant={selected ? 'filled' : 'outlined'}
                sx={selected
                  ? { bgcolor: TEAL, color: '#fff', cursor: 'pointer', '&:hover': { bgcolor: TEAL_DARK } }
                  : { cursor: 'pointer', '&:hover': { bgcolor: TEAL_LIGHT } }
                }
              />
            );
            return tooltip
              ? <Tooltip key={spec} title={tooltip} arrow placement="top">{chip}</Tooltip>
              : chip;
          })}
        </Box>

        {/* Custom specialization input */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <TextField
            size="small"
            placeholder="Add custom specialization…"
            value={customSpecInput}
            onChange={e => setCustomSpecInput(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter' && customSpecInput.trim()) {
                set('specializations', [...formData.specializations, customSpecInput.trim()]);
                setCustomSpecInput('');
              }
            }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined" size="small"
            onClick={() => {
              if (customSpecInput.trim()) {
                set('specializations', [...formData.specializations, customSpecInput.trim()]);
                setCustomSpecInput('');
              }
            }}
            disabled={!customSpecInput.trim()}
            sx={{ borderColor: TEAL, color: TEAL, '&:hover': { borderColor: TEAL_DARK, bgcolor: TEAL_LIGHT } }}
          >
            Add
          </Button>
        </Box>

        {/* License type — verifier / inspector only */}
        {config.hasLicenseType && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
              Professional License / Credential Type
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
              {LICENSE_TYPES.map(lt => {
                const sel = formData.license_type === lt;
                return (
                  <Chip
                    key={lt} label={lt}
                    onClick={() => set('license_type', sel ? '' : lt)}
                    variant={sel ? 'filled' : 'outlined'}
                    sx={sel
                      ? { bgcolor: TEAL, color: '#fff', cursor: 'pointer', '&:hover': { bgcolor: TEAL_DARK } }
                      : { cursor: 'pointer', '&:hover': { bgcolor: TEAL_LIGHT } }
                    }
                  />
                );
              })}
            </Box>
            <TextField
              label="Or enter custom credential type"
              fullWidth size="small"
              value={LICENSE_TYPES.includes(formData.license_type) ? '' : formData.license_type}
              onChange={e => set('license_type', e.target.value)}
              placeholder="e.g., Registered Professional Engineer"
            />
          </>
        )}
      </Box>
    );
  };

  // ─── STEP: CERTIFICATIONS ────────────────────────────────────────────────────

  const openCertModal = () => {
    setCertInput({ name: '', issuing_org: '', date: '' });
    setCertModalOpen(true);
  };
  const saveCert = () => {
    if (!certInput.name.trim()) return;
    set('certifications', [...formData.certifications, { ...certInput }]);
    setCertInput({ name: '', issuing_org: '', date: '' });
    setCertModalOpen(false);
  };

  const renderCertifications = () => (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Experience &amp; Certifications
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        How many years of experience do you have, and what qualifications do you hold?
      </Typography>

      {/* Years of experience — number input, not free text */}
      <TextField
        label="Years of Experience"
        type="number"
        fullWidth
        required
        sx={{ mb: 3 }}
        value={formData.years_experience}
        onChange={e => set('years_experience', e.target.value)}
        inputProps={{ min: 0, max: 60 }}
        helperText="Total years working in your field"
      />

      <Divider sx={{ mb: 2.5 }} />
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
        Certifications &amp; Qualifications
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add professional certifications and qualifications (all optional).
      </Typography>

      {/* Existing certifications */}
      {formData.certifications.map((cert, idx) => (
        <Paper
          key={idx}
          variant="outlined"
          sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <CheckCircle sx={{ color: TEAL, fontSize: 18, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>{cert.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[cert.issuing_org, cert.date].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() =>
              set('certifications', formData.certifications.filter((_, i) => i !== idx))
            }
          >
            <Delete fontSize="small" />
          </IconButton>
        </Paper>
      ))}

      <Button
        variant="contained" size="small"
        startIcon={<Add />}
        onClick={openCertModal}
        sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK }, mt: formData.certifications.length ? 1 : 0 }}
      >
        Add Certification
      </Button>

      {/* Add-certification modal */}
      <Dialog open={certModalOpen} onClose={() => setCertModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
          Add certification
          <IconButton onClick={() => setCertModalOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <TextField
                label="Certification / Qualification Name"
                fullWidth autoFocus
                value={certInput.name}
                onChange={e => setCertInput(p => ({ ...p, name: e.target.value }))}
                onKeyPress={e => { if (e.key === 'Enter') saveCert(); }}
                placeholder="e.g., National Certificate: Construction Supervision"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Issuing Organisation"
                fullWidth
                value={certInput.issuing_org}
                onChange={e => setCertInput(p => ({ ...p, issuing_org: e.target.value }))}
                placeholder="e.g., CITB Zimbabwe"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date Issued"
                fullWidth
                type="date"
                value={certInput.date}
                onChange={e => setCertInput(p => ({ ...p, date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCertModalOpen(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            startIcon={<CheckCircle />}
            onClick={saveCert}
            disabled={!certInput.name.trim()}
            sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK } }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  // ─── STEP: PHOTO & IDENTITY ──────────────────────────────────────────────────

  const renderPhotoAndId = () => {
    const isCompany = formData.account_type === 'company';

    /** Reusable drag-style upload box */
    const UploadBox = ({ label, hint, fileState, inputRef, onSelect, accept }) => (
      <Paper
        variant="outlined"
        onClick={() => inputRef.current?.click()}
        sx={{
          p: 2.5, textAlign: 'center', borderStyle: 'dashed',
          borderColor: fileState ? TEAL : 'divider',
          bgcolor: fileState ? TEAL_LIGHT : '#FAFAFA',
          cursor: 'pointer',
          transition: 'border-color 0.15s, background-color 0.15s',
          '&:hover': { borderColor: TEAL },
        }}
      >
        <input
          ref={inputRef} type="file" accept={accept}
          style={{ display: 'none' }} onChange={onSelect}
        />
        <CloudUpload
          sx={{ fontSize: 36, color: fileState ? TEAL : 'action.disabled', mb: 0.75 }}
        />
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{label}</Typography>
        {fileState ? (
          <Typography variant="caption" color="success.main" display="block" sx={{ mb: 0.75 }}>
            ✓ {fileState.name}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
            {hint}
          </Typography>
        )}
        <Button
          size="small" variant="outlined"
          onClick={e => { e.stopPropagation(); inputRef.current?.click(); }}
          sx={{ borderColor: TEAL, color: TEAL, '&:hover': { bgcolor: TEAL_LIGHT } }}
        >
          {fileState ? 'Change' : 'Upload'}
        </Button>
      </Paper>
    );

    const isContractor = config.saveProfile === 'contractor';
    // Red star marks a mandatory field (a rounded/black star reads as decorative).
    const reqStar = <Box component="span" sx={{ color: '#d32f2f', fontWeight: 700 }}>★</Box>;

    return (
      <Box>
        <Typography variant="h6" fontWeight={700} gutterBottom>Photo &amp; Identity</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: isContractor ? 1.5 : 3 }}>
          Upload a clear profile photo and identity document so clients can trust your profile.
        </Typography>
        {isContractor && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {reqStar} Mandatory · 🌐 Publicly displayed
          </Typography>
        )}
        {hasExistingIdentityDocument && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Identity document already uploaded. You can continue without uploading it again.
          </Alert>
        )}

        {isContractor ? (
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <UploadBox
                label="Business Logo (optional)"
                hint="🌐 Shown on your contractor card. Company logo or your photo. JPG/PNG, max 5 MB"
                fileState={formData.profile_photo_file || (formData.profile_photo_url ? { name: 'Current logo on file' } : null)}
                inputRef={photoRef}
                onSelect={handlePhotoSelect}
                accept="image/*"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <UploadBox
                label={kycWaived ? 'Photo ID (optional)' : <>Photo ID {reqStar}</>}
                hint="Private. Used to verify your identity. National ID · Passport · License — PDF or image, max 10 MB"
                fileState={formData.id_document_file || (hasExistingIdentityDocument ? { name: 'ID on file' } : null)}
                inputRef={idRef}
                onSelect={handleIdSelect}
                accept="image/*,application/pdf"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <UploadBox
                label="Certificate of Incorporation (optional)"
                hint="Private. Proof your business is registered. CR14 / Certificate of Incorporation — PDF or image"
                fileState={formData.business_reg_file}
                inputRef={bizRegRef}
                onSelect={handleBizRegSelect}
                accept="image/*,application/pdf"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <UploadBox
                label="Tax Clearance"
                hint="Optional. Private. ZIMRA tax clearance — PDF or image"
                fileState={formData.tax_clearance_file}
                inputRef={taxRef}
                onSelect={handleTaxClearanceSelect}
                accept="image/*,application/pdf"
              />
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <UploadBox
                label={
                  (config.saveProfile === 'verifier' || config.saveProfile === 'inspector')
                    ? <>{resolvedPhotoLabel()} {reqStar}</>
                    : resolvedPhotoLabel()
                }
                hint="JPG or PNG — max 5 MB"
                fileState={formData.profile_photo_file || (formData.profile_photo_url ? { name: 'Current photo on file' } : null)}
                inputRef={photoRef}
                onSelect={handlePhotoSelect}
                accept="image/*"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <UploadBox
                label={
                  (config.saveProfile === 'verifier' || config.saveProfile === 'inspector')
                    ? <>ID Document {reqStar}</>
                    : 'ID Document (optional)'
                }
                hint="National ID · Passport · License — PDF or image, max 10 MB"
                fileState={formData.id_document_file || (hasExistingIdentityDocument ? { name: 'ID on file' } : null)}
                inputRef={idRef}
                onSelect={handleIdSelect}
                accept="image/*,application/pdf"
              />
            </Grid>
            {isCompany && (
              <Grid item xs={12} sm={6}>
                <UploadBox
                  label="Business Registration (optional)"
                  hint="CR14 or Certificate of Incorporation — PDF or image"
                  fileState={formData.business_reg_file}
                  inputRef={bizRegRef}
                  onSelect={handleBizRegSelect}
                  accept="image/*,application/pdf"
                />
              </Grid>
            )}
          </Grid>
        )}
      </Box>
    );
  };

  // ─── STEP: PROFILE PHOTO (verifier 3-step flow — photo only, no ID) ─────────

  const renderProfilePhoto = () => {
    /** Drag-style upload box (same as renderPhotoAndId but standalone) */
    const UploadBox = ({ label, hint, fileState, inputRef, onSelect, accept }) => (
      <Paper
        variant="outlined"
        onClick={() => inputRef.current?.click()}
        sx={{
          p: 3, textAlign: 'center', borderStyle: 'dashed',
          borderColor: fileState ? TEAL : 'divider',
          bgcolor: fileState ? TEAL_LIGHT : '#FAFAFA',
          cursor: 'pointer',
          transition: 'border-color 0.15s, background-color 0.15s',
          '&:hover': { borderColor: TEAL },
          maxWidth: 400, mx: 'auto',
        }}
      >
        <input
          ref={inputRef} type="file" accept={accept}
          style={{ display: 'none' }} onChange={onSelect}
        />
        <CloudUpload
          sx={{ fontSize: 48, color: fileState ? TEAL : 'action.disabled', mb: 1 }}
        />
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{label}</Typography>
        {fileState ? (
          <Typography variant="caption" color="success.main" display="block" sx={{ mb: 0.75 }}>
            {fileState.name}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
            {hint}
          </Typography>
        )}
        <Button
          size="small" variant="outlined"
          onClick={e => { e.stopPropagation(); inputRef.current?.click(); }}
          sx={{ borderColor: TEAL, color: TEAL, '&:hover': { bgcolor: TEAL_LIGHT } }}
        >
          {fileState ? 'Change' : 'Upload'}
        </Button>
      </Paper>
    );

    return (
      <Box>
        <Typography variant="h6" fontWeight={700} gutterBottom>Profile Photo</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload a profile photo to help clients recognise you. This is optional but highly recommended.
        </Typography>

        <UploadBox
          label="Profile Photo"
          hint="JPG or PNG — max 5 MB"
          fileState={formData.profile_photo_file}
          inputRef={photoRef}
          onSelect={handlePhotoSelect}
          accept="image/*"
        />

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, textAlign: 'center' }}>
          You can upload your national ID and certifications later from your profile settings before submitting your first proposal.
        </Typography>

      </Box>
    );
  };

  // ─── STEP: BIO & REVIEW ──────────────────────────────────────────────────────

  const renderBioAndReview = () => {
    // Step index offset so Edit buttons land on the correct step number
    // contractor: steps are [Choose Tier(0), Basic Info(1), Specs(2), Certs(3), Photo(4), Bio(5)]
    // verifier:   steps are [Basic Info(0), Specs(1), Certs(2), Photo(3), Bio(4)]
    const off = config.skipTier ? 0 : 1;

    return (
      <Box>
        {/* ── Bio section ────────────────────────────────────── */}
        <Box sx={{ mb: 3.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <AutoAwesome sx={{ color: TEAL, mr: 1, fontSize: 22 }} />
            <Typography variant="h6" fontWeight={700}>Your Professional Bio</Typography>
          </Box>

          {bioGenerating ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
              <CircularProgress size={20} sx={{ color: TEAL }} />
              <Typography variant="body2" color="text.secondary">
                Generating your bio with AI…
              </Typography>
            </Box>
          ) : (
            <>
              <TextField
                fullWidth multiline rows={4}
                value={formData.bio}
                onChange={e => set('bio', e.target.value)}
                placeholder="Your professional bio will appear here. You can edit it freely."
                sx={{
                  mb: 0.75,
                  '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: TEAL },
                }}
              />
              {!bioGenerated && (
                <Button
                  size="small" startIcon={<AutoAwesome sx={{ fontSize: '1rem' }} />}
                  onClick={triggerBioGeneration}
                  sx={{ color: TEAL }}
                >
                  Generate Bio
                </Button>
              )}
            </>
          )}
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* ── Review summary ─────────────────────────────────── */}
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Review Your Profile</Typography>

        <Grid container spacing={1.5}>
          {/* Contractor type card */}
          {!config.skipTier && (
            <Grid item xs={12} sm={6}>
              <ReviewSection title="Contractor Type" stepIndex={0} onEdit={jumpToStep}>
                <Typography variant="body2">
                  {config.tiers?.find(t => t.id === formData.tier)?.label || '—'}
                </Typography>
              </ReviewSection>
            </Grid>
          )}

          {/* Basic info */}
          <Grid item xs={12} sm={6}>
            <ReviewSection title="Basic Info" stepIndex={off + 0} onEdit={jumpToStep}>
              <Typography variant="body2" fontWeight={600}>
                {[formData.first_name, formData.last_name].filter(Boolean).join(' ') || '—'}
              </Typography>
              {formData.company_name && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {formData.company_name}
                </Typography>
              )}
              {formData.city && (
                <Typography variant="caption" color="text.secondary" display="block">
                  📍 Based in {formData.city}
                </Typography>
              )}
              {(formData.nationwide || formData.service_areas.length > 0) && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Services:{' '}
                  {formData.nationwide
                    ? 'Nationwide'
                    : formData.service_areas.slice(0, 3).join(', ') +
                      (formData.service_areas.length > 3 ? ` +${formData.service_areas.length - 3} more` : '')}
                </Typography>
              )}
            </ReviewSection>
          </Grid>

          {/* Specializations */}
          <Grid item xs={12} sm={6}>
            <ReviewSection title="Specializations" stepIndex={off + 1} onEdit={jumpToStep}>
              {formData.specializations.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                  {formData.specializations.slice(0, 5).map(s => (
                    <Chip
                      key={s} label={s} size="small"
                      sx={{ bgcolor: TEAL_LIGHT, color: '#1a5c3a', fontSize: '0.65rem' }}
                    />
                  ))}
                  {formData.specializations.length > 5 && (
                    <Chip
                      label={`+${formData.specializations.length - 5} more`}
                      size="small" variant="outlined"
                    />
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">None selected</Typography>
              )}
            </ReviewSection>
          </Grid>

          {/* Experience & certifications */}
          <Grid item xs={12} sm={6}>
            <ReviewSection title="Experience &amp; Certifications" stepIndex={off + 2} onEdit={jumpToStep}>
              <Typography variant="body2">
                {formData.years_experience ? `${formData.years_experience} years` : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formData.certifications.length}{' '}
                certification{formData.certifications.length !== 1 ? 's' : ''} added
              </Typography>
            </ReviewSection>
          </Grid>

          {/* Photo & ID */}
          <Grid item xs={12} sm={6}>
            <ReviewSection title="Photo &amp; ID" stepIndex={off + 3} onEdit={jumpToStep}>
              {(() => {
                const hasPhoto = formData.profile_photo_file || formData.profile_photo_url;
                const hasId = formData.id_document_file || hasExistingIdentityDocument;
                return (
                  <>
                    <Typography variant="body2" color={hasPhoto ? 'success.main' : 'text.secondary'}>
                      {hasPhoto ? `✓ ${resolvedPhotoLabel()} added` : 'No photo added'}
                    </Typography>
                    <Typography variant="caption" color={hasId ? 'success.main' : 'text.secondary'} display="block">
                      {hasId ? '✓ ID document on file' : 'No ID added'}
                    </Typography>
                  </>
                );
              })()}
            </ReviewSection>
          </Grid>
        </Grid>

      </Box>
    );
  };

  // ─── STEP ROUTER ─────────────────────────────────────────────────────────────

  const renderPortfolioStep = () => (
    <PortfolioReferencesStep ref={portfolioStepRef} onChange={setPortfolioRefCounts} color={PRIMARY} />
  );

  const renderStep = (idx) => {
    switch (steps[idx]) {
      case 'Choose Tier':     return renderChooseTier();
      case 'Basic Info':      return renderBasicInfo();
      case 'Specializations': return renderSpecializations();
      case 'Certifications':  return renderCertifications();
      case 'Photo & ID':      return renderPhotoAndId();
      case 'Profile Photo':   return renderProfilePhoto();
      case 'Portfolio': return renderPortfolioStep();
      case 'Bio & Review':    return renderBioAndReview();
      default: return null;
    }
  };

  // ─── RENDER ──────────────────────────────────────────────────────────────────

  // ── Header: title + stepper ──
  const headerBlock = (
    <Box
      sx={{
        px: 3, pt: 2.5, pb: 0,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex', alignItems: 'baseline',
          justifyContent: 'space-between', mb: 0.5,
        }}
      >
        <Typography variant="h5" fontWeight={700}>Complete Your Profile</Typography>
        <Typography variant="caption" color="text.secondary">
          Step {activeStep + 1} of {steps.length}
        </Typography>
      </Box>

      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          mt: 1.5,
          '& .MuiStepIcon-root.Mui-active':    { color: PRIMARY },
          '& .MuiStepIcon-root.Mui-completed': { color: PRIMARY },
        }}
      >
        {steps.map(label => (
          <Step key={label}>
            <StepLabel>
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{label}</Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <LinearProgress
        variant="determinate"
        value={Math.round(((activeStep + 1) / steps.length) * 100)}
        sx={{
          mt: 1.5,
          height: 4,
          borderRadius: 2,
          bgcolor: '#E0E0E0',
          '& .MuiLinearProgress-bar': { bgcolor: PRIMARY, borderRadius: 2 },
        }}
      />
    </Box>
  );

  // ── Step content (errors + current step body) ──
  const stepContent = renderStep(activeStep);

  // Errors render next to the action buttons at the BOTTOM (where the user is when
  // they hit Next/Complete) — not at the top, which is off-screen on a long step.
  const footerError = error ? (
    <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
      {error}
    </Alert>
  ) : null;

  // ── Navigation footer buttons ──
  const footerButtons = (
    <>
      <Button
        onClick={handleBack}
        disabled={activeStep === 0 || saving}
        startIcon={<ArrowBack />}
        sx={{ color: 'text.secondary' }}
      >
        Back
      </Button>

      <Box sx={{ flex: 1 }} />

      {isLastStep ? (
        <Button
          variant="contained"
          size="large"
          onClick={handleComplete}
          disabled={saving || bioGenerating || (steps.includes('Bio & Review') && !formData.bio.trim())}
          startIcon={saving
            ? <CircularProgress size={16} color="inherit" />
            : <CheckCircle />
          }
          sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK }, px: 3 }}
        >
          {saving ? 'Saving…' : 'Complete Setup'}
        </Button>
      ) : (
        <Button
          variant="contained"
          onClick={handleNext}
          endIcon={<ArrowForward />}
          sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK } }}
        >
          Next
        </Button>
      )}
    </>
  );

  // ── Page variant: render inline (no Dialog), sticky footer ──
  if (variant === 'page') {
    return (
      <Box sx={{ maxWidth: 960, mx: 'auto', p: { xs: 2, md: 3 } }}>
        {headerBlock}
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          {stepContent}
        </Box>
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            py: 2,
            mt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {footerError}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {footerButtons}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Dialog
      open
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '80vh', maxHeight: '95vh' } }}
    >
      {/* ── Header: title + stepper ── */}
      {headerBlock}

      {/* ── Step content ── */}
      <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
        {stepContent}
      </DialogContent>

      {/* ── Error (above the action buttons) + navigation ── */}
      {footerError && <Box sx={{ px: 3, pt: 1.5 }}>{footerError}</Box>}
      <DialogActions
        sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}
      >
        {footerButtons}
      </DialogActions>
    </Dialog>
  );
};

export default UnifiedOnboardingModal;
